import {
  ARTIFACT_PATHS,
  MetaArtifact,
  OperatorsArtifact,
  PricesArtifact,
  SiteDetail,
  StationsGeoJson,
  StatusArtifact,
  validate,
} from '@ev/shared'
import type * as ft from 'funtypes'

/**
 * Reads published artifacts.
 *
 * Every response is validated through the frozen runtypes in `@ev/shared`, so a
 * bad publish surfaces here as a named field path rather than as `undefined` in
 * a component three levels down.
 *
 * Base URL: `VITE_ARTIFACTS_BASE_URL` when set, otherwise `/data`, which is
 * where `scripts/sync-fixtures.mjs` puts the local fixture set. Production
 * points at the published bucket; local development gets the fixtures for free.
 */

export const DEFAULT_ARTIFACTS_BASE_URL = '/data'

export interface ArtifactsEnv {
  readonly VITE_ARTIFACTS_BASE_URL?: string | undefined
  readonly [key: string]: unknown
}

export const resolveArtifactsBaseUrl = (env: ArtifactsEnv = import.meta.env): string => {
  const configured = env.VITE_ARTIFACTS_BASE_URL?.trim()
  return configured === undefined || configured === ''
    ? DEFAULT_ARTIFACTS_BASE_URL
    : configured.replace(/\/+$/, '')
}

/**
 * Why a read failed, in the four ways that call for different words to the
 * visitor.
 *
 * `validation` is the one that matters: the file arrived, it was JSON, and it
 * did not match the contract. That is a bad publish, not a bad connection, and
 * telling somebody looking at a broken deploy to check their network wastes
 * their time. The UI picks its copy from this field, never from the message
 * text - which stays English and developer-facing, for the console.
 */
export type ArtifactErrorKind = 'network' | 'http' | 'parse' | 'validation'

export interface ArtifactErrorInit {
  readonly kind: ArtifactErrorKind
  readonly url: string
  /** Artifact path, e.g. `stations.geojson`. Names which file is broken. */
  readonly source: string
  /** HTTP status, when there was a response at all. */
  readonly status?: number
  /** Validator output: the field path that did not match. */
  readonly detail?: string
  readonly cause?: unknown
}

export class ArtifactError extends Error {
  readonly kind: ArtifactErrorKind
  readonly url: string
  readonly source: string
  readonly status: number | undefined
  readonly detail: string | undefined

  constructor(message: string, init: ArtifactErrorInit) {
    super(message, { cause: init.cause })
    this.name = 'ArtifactError'
    this.kind = init.kind
    this.url = init.url
    this.source = init.source
    this.status = init.status
    this.detail = init.detail
  }

  /** A missing artifact is "this is gone", not "the network is broken". */
  get notFound(): boolean {
    return this.status === 404
  }
}

export const isArtifactError = (error: unknown): error is ArtifactError =>
  error instanceof ArtifactError

export interface ArtifactClientOptions {
  readonly baseUrl?: string
  /** Injected for testability; defaults to the global `fetch`. */
  readonly fetch?: typeof globalThis.fetch
  /** Passed through to `fetch`, so a caller can cancel in-flight loads. */
  readonly signal?: AbortSignal
}

export interface ArtifactClient {
  readonly baseUrl: string
  fetchStations(signal?: AbortSignal): Promise<StationsGeoJson>
  fetchOperators(signal?: AbortSignal): Promise<OperatorsArtifact>
  fetchMeta(signal?: AbortSignal): Promise<MetaArtifact>
  fetchStatus(signal?: AbortSignal): Promise<StatusArtifact>
  fetchPrices(signal?: AbortSignal): Promise<PricesArtifact>
  fetchSite(siteId: string, signal?: AbortSignal): Promise<SiteDetail>
}

export const createArtifactClient = (options: ArtifactClientOptions = {}): ArtifactClient => {
  const baseUrl = options.baseUrl ?? resolveArtifactsBaseUrl()

  /**
   * Resolved per call rather than captured here. The shared client is built
   * when this module first loads, which is before a service worker, a test
   * harness or anything else has had a chance to install its own `fetch` -
   * binding it now would pin every request in the app to whatever was on
   * `globalThis` at import time.
   */
  const doFetch: typeof globalThis.fetch =
    options.fetch ?? ((input, init) => globalThis.fetch(input, init))

  const read = async <T>(
    relativePath: string,
    runtype: ft.RuntypeBase<T>,
    signal: AbortSignal | undefined,
  ): Promise<T> => {
    const url = `${baseUrl}/${relativePath}`
    const where = { url, source: relativePath } as const

    let response: Response
    try {
      response = await doFetch(url, { signal: signal ?? options.signal })
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
      throw new ArtifactError(`${url} could not be reached`, { ...where, kind: 'network', cause })
    }

    if (!response.ok)
      throw new ArtifactError(`${url} returned ${response.status} ${response.statusText}`, {
        ...where,
        kind: 'http',
        status: response.status,
      })

    let payload: unknown
    try {
      payload = await response.json()
    } catch (cause) {
      throw new ArtifactError(`${url} is not valid JSON`, {
        ...where,
        kind: 'parse',
        status: response.status,
        cause,
      })
    }

    const result = validate(runtype, payload, { source: relativePath })
    if (!result.ok)
      throw new ArtifactError(`${url} does not match its contract: ${result.error}`, {
        ...where,
        kind: 'validation',
        status: response.status,
        detail: result.error,
      })

    return result.value
  }

  return {
    baseUrl,
    fetchStations: (signal) => read(ARTIFACT_PATHS.stations, StationsGeoJson, signal),
    fetchOperators: (signal) => read(ARTIFACT_PATHS.operators, OperatorsArtifact, signal),
    fetchMeta: (signal) => read(ARTIFACT_PATHS.meta, MetaArtifact, signal),
    fetchStatus: (signal) => read(ARTIFACT_PATHS.status, StatusArtifact, signal),
    fetchPrices: (signal) => read(ARTIFACT_PATHS.prices, PricesArtifact, signal),
    fetchSite: (siteId, signal) => read(ARTIFACT_PATHS.site(siteId), SiteDetail, signal),
  }
}

/** The client the app uses. Tests build their own with an injected `fetch`. */
export const artifactClient: ArtifactClient = createArtifactClient()
